import type { EntityType } from '../../../shared/services/clientDataMappingService';

const MATCH_NODES = [
  'match_id',
  'slug',
  'seo_title',
  'seo_description',
  'seo_keywords',
  'custom_url',
  'published_at',
  'match.name',
  'match.start_date',
  'match.end_date',
  'match.venue',
  'match.home_score',
  'match.away_score',
  'match.home_penalty_score',
  'match.away_penalty_score',
  'match.status',
  'match.team.name',
  'match.team.short_name',
  'match.team.logo',
  'match.team.country',
  'match.team.city',
  'match.team.venue',
  'match.team.date_established',
  'match.team.website',
  'match.team.lineup.starting_xi',
  'match.team.lineup.bench',
  'match.team.lineup.captain',
  'match.team.lineup.starting_xi[].name',
  'match.team.lineup.starting_xi[].player_id',
  'match.team.lineup.starting_xi[].position',
  'match.team.lineup.starting_xi[].kit_number',
  'match.team.lineup.starting_xi[].identification_description',
  'match.team.lineup.starting_xi[].identification_screenshot_url',
  'match.team.lineup.starting_xi[].nationality',
  'match.team.lineup.starting_xi[].profile_image',
  'match.team.lineup.starting_xi[].height_cm',
  'match.team.lineup.starting_xi[].weight_kg',
  'match.team.lineup.starting_xi[].preferred_foot',
  'match.team.lineup.starting_xi[].is_captain',
  'match.team.lineup.bench[].name',
  'match.team.lineup.bench[].player_id',
  'match.team.lineup.bench[].position',
  'match.team.lineup.bench[].kit_number',
  'match.team.lineup.bench[].identification_description',
  'match.team.lineup.bench[].identification_screenshot_url',
  'match.team.lineup.bench[].nationality',
  'match.team.lineup.bench[].profile_image',
  'match.team.lineup.bench[].height_cm',
  'match.team.lineup.bench[].weight_kg',
  'match.team.lineup.bench[].preferred_foot',
  'match.team.lineup.bench[].is_captain',
  'match.team.lineup.captain.name',
  'match.team.lineup.captain.player_id',
  'match.team.lineup.captain.position',
  'match.team.lineup.captain.kit_number',
  'match.team.lineup.captain.identification_description',
  'match.team.lineup.captain.identification_screenshot_url',
  'match.team.lineup.captain.nationality',
  'match.team.lineup.captain.profile_image',
  'match.team.lineup.captain.height_cm',
  'match.team.lineup.captain.weight_kg',
  'match.team.lineup.captain.preferred_foot',
  'match.team.lineup.captain.is_captain',
  'match.team.formation.formation',
  'match.team.formation.manager',
  'match.tournament.name',
  'match.tournament.logo',
  'match.tournament.country',
  'match.season.name',
  'match.season.start_date',
  'match.season.end_date',
  'match.officials.referee',
  'match.officials.assistant_1',
  'match.officials.assistant_2',
  'match.officials.var',
  'match.officials.fourth',
  'match.score_summary.team.team_name',
  'match.score_summary.team.goals[].player_name',
  'match.score_summary.team.goals[].player_id',
  'match.score_summary.team.goals[].match_time',
  'match.score_summary.team.goals[].event_type',
  'match.score_summary.team.goals[].display_name',
  'match.score_summary.team.goals[].is_own_goal',
  'key_events[].minute',
  'key_events[].timestamp',
  'key_events[].event_type',
  'key_events[].display_name',
  'key_events[].category',
  'key_events[].team',
  'key_events[].team_logo',
  'key_events[].primary_player',
  'key_events[].primary_player_id',
  'key_events[].primary_player_kit',
  'key_events[].secondary_player',
  'key_events[].secondary_player_id',
  'key_events[].secondary_player_kit',
  'key_events[].tags[]',
];

const PLAYER_NODES = [
  'player_id',
  'team_id',
  'full_name',
  'kit_number',
  'team',
  'position',
  'is_captain',
  'is_starting',
  'is_bench',
  'player_screenshot',
  'player_identification_description',
  'nationality',
  'height_cm',
  'weight_kg',
  'preferred_foot',
  'stats',
  'stats[].stat_key',
  'stats[].display_name',
  'stats[].category',
  'stats[].value',
  'stats[].event_result',
  'stats[].is_calculated',
];

const HIGHLIGHT_NODES = [
  'id',
  'name',
  'video_url',
  'duration_seconds',
  'status',
  'player_ids',
  'social_media_videos[].platform',
  'social_media_videos[].platform_video_id',
  'social_media_videos[].platform_url',
  'social_media_videos[].upload_status',
];

const TACTICAL_CHART_NODES = [
  'id',
  'name',
  'type',
  'view_types',
  'url',
  'player_ids',
];

const MATCH_LIST_NODES = [
  'slug',
  'seo_title',
  'seo_description',
  'seo_keywords',
  'custom_url',
  'published_at',
  'match.id',
  'match.name',
  'match.start_date',
  'match.end_date',
  'match.venue',
  'match.home_score',
  'match.away_score',
  'match.home_penalty_score',
  'match.away_penalty_score',
  'match.status',
  'match.processing_status',
  'match.team.name',
  'match.team.logo',
  'match.team.country',
  'match.tournament.name',
  'match.tournament.logo',
  'match.tournament.country',
  'match.season.name',
  'match.season.start_date',
  'match.season.end_date',
];

const CREATE_MATCH_REQUEST_NODES = [
  'tournament_id',
  'season_id',
  'home_team_id',
  'away_team_id',
  'name',
  'start_date',
  'venue',
  'external_match_id',
];

const CREATE_MATCH_RESPONSE_NODES = [
  'success',
  'message',
  'matchId',
];

const MATCH_STATS_NODES = [
  'statistics.home_team_stats',
  'statistics.away_team_stats',
  'statistics[].stat_key',
  'statistics[].display_name',
  'statistics[].category',
  'statistics[].value',
  'statistics[].event_result',
  'statistics[].top_stats',
];

export function getInternalNodeOptions(entityType: EntityType): string[] {
  switch (entityType) {
    case 'match':
      return MATCH_NODES;
    case 'match_stats':
      return MATCH_STATS_NODES;
    case 'player':
      return PLAYER_NODES;
    case 'highlight':
      return HIGHLIGHT_NODES;
    case 'tactical_chart':
      return TACTICAL_CHART_NODES;
    case 'match_list':
      return MATCH_LIST_NODES;
    case 'create_match':
      return CREATE_MATCH_RESPONSE_NODES;
    case 'stat':
      return [];
    default:
      return [];
  }
}

export function getInternalNodeOptionsByScope(
  entityType: EntityType,
  scope: 'request' | 'response' | 'both'
): string[] {
  if (entityType !== 'create_match') return getInternalNodeOptions(entityType);
  if (scope === 'request') return CREATE_MATCH_REQUEST_NODES;
  if (scope === 'response') return CREATE_MATCH_RESPONSE_NODES;
  return Array.from(new Set([...CREATE_MATCH_REQUEST_NODES, ...CREATE_MATCH_RESPONSE_NODES]));
}
